import React from 'react'
import ReactDOM from 'react-dom'
import Header from '../common/Header'
import { useState, useEffect, useCallback } from 'react'
import { Send, Trash2, ChevronLeft, ChevronRight, Eye, EyeOff, User, Check, ToggleRight, CheckCircle, MoreVertical } from 'lucide-react'
import { Card, CardTitle, KpiCard, TableHeader, TableCell, Badge } from '../UI'
import DeletePopUp from '../Modals/DeletePopUp'
import ActivatePopUp from '../Modals/ActivatePopUp'
import { getAllAdmins, createAdmin, updateAdmin, deleteAdmin, deleteAdminPermanent, activateAdmin, getAgencyStats } from '../../requests'
import { validateName, validateEmail } from '../helper/validators'
import { notifyError, notifySuccess } from '../../utils/helper'
import { useDispatch } from 'react-redux'
import { startLoader, stopLoader } from '../../_store/reducers/loaderReducer'
import { deleteAuthToken } from '../../utils/helper'
import { removeUserInformation } from '../../_store/reducers/userReducer'
import { useNavigate } from 'react-router-dom'
import inviteStyles from '../../_css/Invite.module.css'
import useRole from '../../hooks/getRole'
import SearchableDrp from '../common/SearchableDrp'
import { USER_ROLES } from './constants'
import { toCapitalize } from '../helper/formatters'

const PER_PAGE = 5

const Index = () => {
  const role = useRole()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [admins, setAdmins] = useState([])
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    perPage: PER_PAGE,
    hasNextPage: false,
    hasPrevPage: false,
    nextPage: null,
    prevPage: null,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Create form
  const [fullName, setFullName] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [createLoading, setCreateLoading] = useState(false)
  const [createError, setCreateError] = useState(null)
  const [inviteRole, setInviteRole] = useState('admin')

  // Delete confirm popup
  const [deleteTarget, setDeleteTarget] = useState(null)
  // Activate confirm popup
  const [activateTarget, setActivateTarget] = useState(null)
  // Permanent delete confirm popup
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState(null)
  const [showInvite, setShowInvite] = useState(false)

  // Actions dropdown (which row's menu is open)
  const [openActionsId, setOpenActionsId] = useState(null)
  const [dropdownAnchor, setDropdownAnchor] = useState(null)

  // Edit admin
  const [editingAdmin, setEditingAdmin] = useState(null)
  const [editName, setEditName] = useState('')
  const [editRole, setEditRole] = useState('admin')
  const [editLoading, setEditLoading] = useState(false)
  const [editError, setEditError] = useState(null)
  const [adminStats, setAdminStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  })
  const fetchAdmins = useCallback(async (page = 1) => {
    setLoading(true)
    setError(null)
    try {
      const res = await getAllAdmins({ page, limit: PER_PAGE })
      const { data = [], pagination: pag } = res?.data ?? {}
      setAdmins(Array.isArray(data) ? data : [])
      setPagination(pag ?? {
        total: 0,
        totalPages: 0,
        currentPage: page,
        perPage: PER_PAGE,
        hasNextPage: false,
        hasPrevPage: false,
        nextPage: null,
        prevPage: null,
      })
    } catch (err) {
      //is status code 401, then redirect to login page
      if (err?.response?.status === 401) {
        deleteAuthToken()
        dispatch(removeUserInformation())
        navigate('/login')
        notifyError('Session expired. Please login again.'||err?.response?.data?.message)
      }
      else {
        setError(err?.response?.data?.message || err?.message || 'Failed to load admins')
        setAdmins([])
      }
    } finally {
      setLoading(false)
    }
  }, [dispatch, navigate])

  const fetchAdminStats = useCallback(async () => {
  
    try {
      const res = await getAgencyStats()
      if (res?.status<400) {
        const data = res?.data ?? res
        setAdminStats(data?.user)
      }
    } catch (error) {
      console.error('Failed to fetch admin stats', error)
    }
  },[])


  useEffect(() => {
    fetchAdmins(pagination.currentPage || 1)
    fetchAdminStats()
  }, [fetchAdmins, fetchAdminStats, pagination.currentPage])

  const validateCreateForm = () => {
    if (!fullName.trim()) {
      notifyError('Please enter full name')
      return false
    }
    if (fullName.length < 3) {
      notifyError('Full name must be at least 3 characters')
      return false
    }
    if (!validateName(fullName)) {
      notifyError('Please enter a valid full name without special characters or numbers')
      return false
    }
    if (!inviteEmail.trim()) {
      notifyError('Please enter email address')
      return false
    }
    if (!validateEmail(inviteEmail)) {
      notifyError('Please enter a valid email address')
      return false
    }
    // if (!password.trim()) {
    //   notifyError('Please enter password')
    //   return false
    // }
    // if (password.length < 8) {
    //   notifyError('Password must be at least 8 characters')
    //   return false
    // }
    if (!inviteRole.trim()) {
      notifyError('Please select role')
      return false
    }
    return true
  }

  const handleInvite = async (e) => {
    e.preventDefault()

    const name = fullName.trim()

    if (!validateCreateForm()) return
    const email = inviteEmail.trim().toLowerCase()
    const pwd = password.trim()
    setCreateLoading(true)
    setCreateError(null)
    dispatch(startLoader())
    try {
      const payload = {
        full_name: name,
        email,
        password: pwd,
        role: inviteRole.trim()
      }
    
      const response = await createAdmin(payload)
      if (response?.status < 400) {
        notifySuccess("User invited successfully, Activation link sent to the user's email" || 'Admin created successfully')
        setFullName('')
        setInviteEmail('')
        setPassword('')
        setInviteRole('user')
        setCreateError(null)
        setShowPassword(false)
        await fetchAdmins(pagination.currentPage)
        await fetchAdminStats()
        setShowInvite(false)
      }
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Failed to create admin')
    } finally {
      setCreateLoading(false)
      dispatch(stopLoader())
    }
  }

  const openEdit = (admin) => {
    setShowInvite(false)
    setEditingAdmin(admin)
    setEditName(admin.full_name || '')
    setEditRole(admin.role || 'admin')
    setEditError(null)
  }

  const closeEdit = () => {
    setEditingAdmin(null)
    setEditName('')
    setEditRole('admin')
    setEditError(null)
  }

  const validateEditForm = () => {
    if (!editName.trim()) {
      notifyError('Please enter full name')
      return false
    }
    if (editName.length < 3) {
      notifyError('Full name must be at least 3 characters')
      return false
    }
    if (!validateName(editName)) {
      notifyError('Please enter a valid full name without special characters or numbers')
      return false
    }
    if (!editRole.trim()) {
      notifyError('Please select role')
      return false
    }
    return true
  }

  const handleUpdateAdmin = async (e) => {
    e.preventDefault()
    if (!editingAdmin?.id) return
    if (!validateEditForm()) return
    const name = editName.trim()
    setEditLoading(true)
    setEditError(null)
    dispatch(startLoader())
    try {
      const response = await updateAdmin({ id: editingAdmin.id, full_name: name, role: editRole.trim() })
      if (response?.status < 400) {
        notifySuccess(response?.data?.message || 'Admin updated successfully')
        closeEdit()
        await fetchAdmins(pagination.currentPage)
        await fetchAdminStats()
      }
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Failed to update admin')
    } finally {
      setEditLoading(false)
      dispatch(stopLoader())
    }
  }

  const openDeleteConfirm = (admin) => {
    setDeleteTarget({ id: admin.id, full_name: admin.full_name })
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return
    const id = deleteTarget.id
    try {
     const response = await deleteAdmin(id)
     if (response?.status < 400) {
      notifySuccess(response?.data?.message || 'Admin deleted successfully')
      setDeleteTarget(null)
      await fetchAdmins(pagination.currentPage)
      await fetchAdminStats()
     }
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Failed to delete admin')
      setDeleteTarget(null)
    }
  }

  const openPermanentDeleteConfirm = (admin) => {
    setPermanentDeleteTarget({ id: admin.id, full_name: admin.full_name })
  }

  const handleConfirmPermanentDelete = async () => {
    if (!permanentDeleteTarget?.id) return
    const id = permanentDeleteTarget.id
    try {
      const response = await deleteAdminPermanent(id)
      if (response?.status < 400) {
        notifySuccess(response?.data?.message || 'Admin permanently deleted')
        setPermanentDeleteTarget(null)
        await fetchAdmins(pagination.currentPage)
        await fetchAdminStats()
      }
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Failed to delete admin')
      setPermanentDeleteTarget(null)
    }
  }

  const handleBtnClick = () => {
    if(editingAdmin) {
      setEditingAdmin(null)
      setEditName('')
      setEditRole('admin')
      setEditError(null)
    }

    else{
      setFullName('')
      setInviteEmail('')
      setPassword('')
      setInviteRole('user')
      setCreateError(null)
      setShowPassword(false)
      setShowInvite(!showInvite)
    }
   
  }

  const goToPage = (page) => {
    if (page >= 1 && page <= pagination.totalPages) fetchAdmins(page)
  }

  const openActivateConfirm = (admin) => {
    setActivateTarget({ id: admin.id, full_name: admin.full_name })
  }

  const handleConfirmActivate = async () => {
    if (!activateTarget?.id) return
    const id = activateTarget.id
    try {
      const response = await activateAdmin(id)
      if (response?.status < 400) {
        notifySuccess(response?.data?.message || 'Admin activated successfully')
        setActivateTarget(null)
        await fetchAdmins(pagination.currentPage)
        await fetchAdminStats()
      }
    } catch (err) {
      setActivateTarget(null)
      notifyError(err?.response?.data?.message || 'Failed to activate admin')
    }
  }

  const activeCount = admins.filter((a) => a.isActive).length
  const inactiveCount = admins.length - activeCount

  return (
    <div>
      {/* <Header userType={role} headerTitle="Users" /> */}
      <div className={inviteStyles.page}>
        <div className={inviteStyles.headerRow}>
          <div className={inviteStyles.titleBlock}>
            <h1>Invite & Manage Users</h1>
            <p>Manage team members and their access levels</p>
          </div>
          <button type="button" onClick={handleBtnClick} className={inviteStyles.primaryBtn}>
            {editingAdmin ? 'Close Edit Form' : showInvite ? 'Close Invite Form' : 'Invite User +'}
          </button>
        </div>

        {error && (
          <div className={inviteStyles.errorBox}>
            {error}
          </div>
        )}

        <div className={inviteStyles.kpiGrid}>
            <KpiCard
              label="Total members"
              value={adminStats?.total}
              sub={`${activeCount} active in this page`}
              icon="👥"
            />
            <KpiCard
              label="Active members"
              value={adminStats?.active}
              sub="Currently active"
              icon="🔑"
            />
            <KpiCard
              label="Inactive members"
              value={adminStats?.inactive}
              sub="Not active"
              icon="👤"
            />
          </div>

        {editingAdmin && (
          <Card>
            <CardTitle sub={`Editing ${editingAdmin.email}`}>
              Edit Team Member
            </CardTitle>
            <form onSubmit={handleUpdateAdmin}>
              <div className={inviteStyles.formGrid2}>
                <div className={inviteStyles.formField}>
                  <label htmlFor="editFullName" className={inviteStyles.label}>
                    Full Name <span className={inviteStyles.labelRequired}>*</span>
                  </label>
                  <input
                    id="editFullName"
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Full name"
                    className={inviteStyles.input}
                    required
                  />
                </div>
                <div className={inviteStyles.formField}>
                  <label className={inviteStyles.label}>Email (read-only)</label>
                  <input
                    type="text"
                    value={editingAdmin.email || ''}
                    readOnly
                    disabled
                    className={`${inviteStyles.input} ${inviteStyles.inputReadOnly}`}
                  />
                </div>
                <div className={inviteStyles.formField}>
                  <label htmlFor="editRole" className={inviteStyles.label}>Role <span className={inviteStyles.labelRequired}>*</span></label>
                  <SearchableDrp
                    options={USER_ROLES}
                    value={editRole}
                    onChange={(v) => setEditRole(v)}
                    placeholder="Select role..."
                    icon={<User size={16} color="#6b7a90" />}
                    searchable={false}
                  />
                </div>
              </div>
              {editError && <p className={inviteStyles.formError}>{editError}</p>}
              <div className={inviteStyles.formActions}>
                <button type="submit" disabled={editLoading} className={inviteStyles.submitBtn}>
                  <Send size={14} />
                  {editLoading ? 'Saving…' : 'Save Changes'}
                </button>
                <button type="button" onClick={closeEdit} className={inviteStyles.secondaryBtn}>
                  Cancel
                </button>
              </div>
            </form>
          </Card>
        )}

        {showInvite && (
          <Card>
            <CardTitle sub="The user will receive an email with link to set their password">
              Invite New Team Member
            </CardTitle>
            <form onSubmit={handleInvite}>
              <div className={inviteStyles.formGrid3}>
                <div className={inviteStyles.formField}>
                  <label htmlFor="fullName" className={inviteStyles.label}>
                    Full Name <span className={inviteStyles.labelRequired}>*</span>
                  </label>
                  <input
                    id="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Trevor Phillips"
                    className={inviteStyles.input}
                    required
                  />
                </div>
                <div className={inviteStyles.formField}>
                  <label htmlFor="inviteEmail" className={inviteStyles.label}>
                    Email Address <span className={inviteStyles.labelRequired}>*</span>
                  </label>
                  <input
                    id="inviteEmail"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="name@company.com"
                    className={inviteStyles.input}
                    required
                  />
                </div>
                {/* <div className={inviteStyles.formField}>
                  <label htmlFor="password" className={inviteStyles.label}>
                    Password <span className={inviteStyles.labelRequired}>*</span>
                  </label>
                  <div className={inviteStyles.passwordWrap}>
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className={inviteStyles.input}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((p) => !p)}
                      className={inviteStyles.passwordToggle}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                    </button>
                  </div>
                </div> */}

                <div className={inviteStyles.formField}>
                  <label htmlFor="role" className={inviteStyles.label}>Role <span className={inviteStyles.labelRequired}>*</span></label>
                  <SearchableDrp
                    options={USER_ROLES}
                    value={inviteRole}
                    onChange={(v) => setInviteRole(v)}
                    placeholder="Select role..."
                    icon= {<User size={16} color="#6b7a90" />}
                    searchable={false}
                  />
                </div>
              </div>
              {createError && <p className={inviteStyles.formError}>{createError}</p>}
              <div className={inviteStyles.formActions}>
                <button type="submit" disabled={createLoading} className={inviteStyles.submitBtn}>
                  <Send size={14} />
                  {createLoading ? 'Sending…' : 'Send Invitation'}
                </button>
                <button type="button" onClick={() => setShowInvite(false)} className={inviteStyles.secondaryBtn}>
                  Cancel
                </button>
              </div>
            </form>
          </Card>
        )}

        <Card>
          <CardTitle>Team Members</CardTitle>
          {loading ? (
            <p className={inviteStyles.loadingText}>Loading…</p>
          ) : admins.length === 0 ? (
            <p className={inviteStyles.emptyText}>No team members yet</p>
          ) : (
            <>
              <div className={inviteStyles.tableWrap}>
                <table className={inviteStyles.table}>
                    <thead>
                      <TableHeader
                        columns={['Name', 'Email', 'Role', 'Added On', 'Status', 'Actions']}
                      />
                    </thead>
                    <tbody>
                      {admins.map((admin) => (
                        <tr key={admin.id}>
                          <TableCell bold>{admin.full_name}</TableCell>
                          <TableCell>{admin.email}</TableCell>
                          <TableCell>
                            <Badge color={admin.role === 'admin' ? '#f97316' : '#14B8A6'}>{toCapitalize(admin.role)}</Badge>
                          </TableCell>
                          <TableCell>
                            {admin.createdAt
                              ? new Date(admin.createdAt).toLocaleDateString()
                              : '—'}
                          </TableCell>
                          <TableCell>
                            <Badge
                              color={
                                admin.is_deleted
                                  ? '#ef4444'
                                  : admin.isActive
                                    ? '#22c55e'
                                    : '#6b7280'
                              }
                            >
                              {admin.is_deleted ? 'Deleted' : admin.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {!admin.is_deleted && (
                              <div className={inviteStyles.actionsCell}>
                                <div className={inviteStyles.dropdownWrap}>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      const rect = e.currentTarget.getBoundingClientRect()
                                      if (openActionsId === admin.id) {
                                        setOpenActionsId(null)
                                        setDropdownAnchor(null)
                                      } else {
                                        setDropdownAnchor({ top: rect.bottom, right: rect.right, left: rect.left })
                                        setOpenActionsId(admin.id)
                                      }
                                    }}
                                    className={inviteStyles.actionIcon}
                                    title="Actions"
                                    aria-haspopup="true"
                                    aria-expanded={openActionsId === admin.id}
                                  >
                                    <MoreVertical size={18} />
                                  </button>
                                  {openActionsId === admin.id &&
                                    dropdownAnchor &&
                                    ReactDOM.createPortal(
                                      <>
                                        <div
                                          className={inviteStyles.dropdownBackdrop}
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            setOpenActionsId(null)
                                            setDropdownAnchor(null)
                                          }}
                                          aria-hidden="true"
                                        />
                                        <ul
                                          className={inviteStyles.dropdownMenu}
                                          role="menu"
                                          style={{
                                            top: dropdownAnchor.top + 4,
                                            right: typeof window !== 'undefined' ? window.innerWidth - dropdownAnchor.right : 0,
                                          }}
                                        >
                                          <li role="none">
                                            <button
                                              type="button"
                                              role="menuitem"
                                              className={inviteStyles.dropdownItem}
                                              onClick={() => {
                                                openEdit(admin)
                                                setOpenActionsId(null)
                                                setDropdownAnchor(null)
                                              }}
                                            >
                                              Edit
                                            </button>
                                          </li>
                                          <li role="none">
                                            <button
                                              type="button"
                                              role="menuitem"
                                              className={admin.isActive ? inviteStyles.dropdownItemDanger : inviteStyles.dropdownItem}
                                              onClick={() => {
                                                if (admin.isActive) {
                                                  openDeleteConfirm(admin)
                                                } else {
                                                  openActivateConfirm(admin)
                                                }
                                                setOpenActionsId(null)
                                                setDropdownAnchor(null)
                                              }}
                                            >
                                              {admin.isActive ? 'Deactivate' : 'Activate'}
                                            </button>
                                          </li>
                                          <li role="none">
                                            <button
                                              type="button"
                                              role="menuitem"
                                              className={inviteStyles.dropdownItemDanger}
                                              onClick={() => {
                                                openPermanentDeleteConfirm(admin)
                                                setOpenActionsId(null)
                                                setDropdownAnchor(null)
                                              }}
                                            >
                                              Delete
                                            </button>
                                          </li>
                                        </ul>
                                      </>,
                                      document.body
                                    )}
                                </div>
                              </div>
                            )}
                          </TableCell>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              {pagination.totalPages > 1 && (
                <div className={inviteStyles.pagination}>
                  <p className={inviteStyles.paginationInfo}>
                    Page {pagination.currentPage} of {pagination.totalPages} ({pagination.total} total)
                  </p>
                  <div className={inviteStyles.paginationBtns}>
                    <button
                      type="button"
                      onClick={() => goToPage(pagination.currentPage - 1)}
                      disabled={!pagination.hasPrevPage}
                      className={inviteStyles.pageBtn}
                    >
                      <ChevronLeft size={16}  />
                    </button>
                    <button
                      type="button"
                      onClick={() => goToPage(pagination.currentPage + 1)}
                      disabled={!pagination.hasNextPage}
                      className={inviteStyles.pageBtn}
                    >
                      <ChevronRight size={16}  />
                    </button>
                  </div>
                </div>
              )}
              </>
            )}
          </Card>

          {/* <Card>
            <CardTitle sub="What each role can do within the platform">Role Permissions</CardTitle>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <TableHeader columns={['Permission', 'Super Admin', 'Agency Admin', 'User']} />
              </thead>
              <tbody>
                {[
                  { perm: 'Manage agencies', sa: true, aa: false, u: false },
                  { perm: 'Manage brands', sa: true, aa: true, u: false },
                  { perm: 'Manage users', sa: true, aa: true, u: false },
                  { perm: 'Create & configure simulations', sa: true, aa: true, u: true },
                  { perm: 'Run simulations', sa: true, aa: true, u: true },
                  { perm: 'View results & insights', sa: true, aa: true, u: true },
                  { perm: 'View cost dashboard', sa: true, aa: true, u: false },
                  { perm: 'Export reports', sa: true, aa: true, u: true },
                ].map((r, i) => (
                  <tr key={i}>
                    <TableCell>{r.perm}</TableCell>
                    <TableCell style={{ textAlign: 'left' }}>
                      <span style={{ color: r.sa ? '#22c55e' : '#ef4444' }}>
                        {r.sa ? '✓' : '✗'}
                      </span>
                    </TableCell>
                    <TableCell style={{ textAlign: 'left' }}>
                      <span style={{ color: r.aa ? '#22c55e' : '#ef4444' }}>
                        {r.aa ? '✓' : '✗'}
                      </span>
                    </TableCell>
                    <TableCell style={{ textAlign: 'left' }}>
                      <span style={{ color: r.u ? '#22c55e' : '#ef4444' }}>
                        {r.u ? '✓' : '✗'}
                      </span>
                    </TableCell>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card> */}
      </div>

      {deleteTarget && (
        <DeletePopUp
          title="Deactivate admin"
          message={`Are you sure you want to deactivate "${deleteTarget.full_name}"? They will lose access to the platform.`}
          onClose={() => setDeleteTarget(null)}
          onDelete={handleConfirmDelete}
        />
      )}

      {activateTarget && (
        <ActivatePopUp
          title="Activate admin"
          message={`Are you sure you want to activate "${activateTarget.full_name}"? They will regain access to the platform.`}
          onClose={() => setActivateTarget(null)}
          onActivate={handleConfirmActivate}
        />
      )}

      {permanentDeleteTarget && (
        <DeletePopUp
          variant="delete"
          title="Delete admin"
          message={`This will permanently delete "${permanentDeleteTarget.full_name}" and all of their simulations. This cannot be undone.`}
          onClose={() => setPermanentDeleteTarget(null)}
          onDelete={handleConfirmPermanentDelete}
        />
      )}

    </div>
  )
}

export default Index
